import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Send, Trash2, Bot, User, Sparkles, ShieldAlert, Search, X } from "lucide-react";
import { toast } from "sonner";

const typeStyle = {
  chat: "text-foreground",
  bot: "text-cookie",
  ai: "text-primary",
  system: "text-purple-400",
};

function LogRow({ e }) {
  const Icon = e.type === "ai" ? Sparkles : e.type === "bot" ? Bot : e.type === "system" ? ShieldAlert : User;
  return (
    <div className="flex gap-2 px-3 py-1.5 hover:bg-white/5 rounded animate-fade-up" data-testid="log-row">
      <Icon className={`w-3.5 h-3.5 mt-1 shrink-0 ${typeStyle[e.type]}`} />
      <div className="min-w-0">
        <span className={`font-semibold text-sm ${typeStyle[e.type]}`}>{e.username}</span>
        <span className={`text-sm ml-2 break-words ${e.flagged ? "line-through text-red-500" : "text-foreground/90"}`}>
          {e.message}
        </span>
      </div>
    </div>
  );
}

export default function LiveChat() {
  const [username, setUsername] = useState("random_rob");
  const [message, setMessage] = useState("");
  const [full, setFull] = useState([]);
  const [bot, setBot] = useState([]);
  const [users, setUsers] = useState([]);
  const [sending, setSending] = useState(false);
  const [search, setSearch] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const fullRef = useRef(null);

  const load = async () => {
    const [f, b, u] = await Promise.all([
      api.get("/logs/full"),
      api.get("/logs/bot"),
      api.get("/users"),
    ]);
    setFull(f.data);
    setBot(b.data);
    setUsers(u.data);
  };

  const runSearch = async (term) => {
    if (!term.trim()) { setSearchResults(null); return; }
    const r = await api.get(`/logs/full?q=${encodeURIComponent(term)}`);
    setSearchResults(r.data);
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (fullRef.current) fullRef.current.scrollTop = fullRef.current.scrollHeight;
  }, [full]);

  const send = async () => {
    if (!message.trim() || !username.trim()) return;
    setSending(true);
    try {
      const res = await api.post("/chat/send", { username, message });
      if (res.data.banned) toast.error("Message blocked — banned word detected");
      setMessage("");
      await load();
    } catch (e) {
      toast.error("Failed to send");
    } finally {
      setSending(false);
    }
  };

  const clearLogs = async () => {
    await api.delete("/logs");
    await load();
    toast.success("Logs cleared");
  };

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-3xl md:text-4xl font-800 font-heading">Live Chat</h1>
        <p className="text-muted-foreground mt-1 text-sm">Simulate viewers and watch Sprinkle Pancake Bot respond in real time.</p>
      </header>

      <div className="bg-[#0a0a0a] border border-white/10 rounded-lg p-4">
        <div className="flex flex-col sm:flex-row gap-2">
          <Input
            data-testid="chat-username-input"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="viewer name"
            className="sm:w-48 bg-secondary border-white/10 focus:ring-primary"
            list="user-list"
          />
          <datalist id="user-list">
            {users.map((u) => <option key={u.id} value={u.username} />)}
          </datalist>
          <Input
            data-testid="chat-message-input"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder="Type a message or command e.g. !talk hi  •  !gamble 20  •  !hug"
            className="flex-1 bg-secondary border-white/10 focus:ring-primary"
          />
          <Button data-testid="chat-send-button" onClick={send} disabled={sending} className="bg-primary hover:bg-primary/90 rounded-lg">
            <Send className="w-4 h-4 mr-1" /> Send
          </Button>
        </div>
        <div className="flex flex-wrap gap-2 mt-3">
          {["!balance", "!talk hi", "!hug", "!givecookies 20", "!fish", "!sellfish", "!gamble 20 cookies", "!mood happy"].map((q) => (
            <button
              key={q}
              onClick={() => setMessage(q)}
              className="text-xs px-2.5 py-1 rounded-full bg-secondary hover:bg-primary/20 text-muted-foreground hover:text-foreground transition-colors duration-200"
              data-testid={`quick-${q}`}
            >
              {q}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-[#0a0a0a] border border-white/10 rounded-lg overflow-hidden flex flex-col h-[520px]">
          <div className="px-4 py-3 border-b border-white/10 bg-black/40 space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="font-heading font-700 text-sm">Full Chat Log</h3>
              <Button variant="ghost" size="sm" onClick={clearLogs} data-testid="clear-logs-button" className="h-7 text-muted-foreground hover:text-red-400">
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                data-testid="log-search-input"
                value={search}
                onChange={(e) => { setSearch(e.target.value); runSearch(e.target.value); }}
                placeholder="Search chat by user or word…"
                className="pl-8 h-8 bg-secondary border-white/10 text-sm focus:ring-primary"
              />
              {search && (
                <button onClick={() => { setSearch(""); setSearchResults(null); }} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" data-testid="clear-search-button">
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
          <div ref={fullRef} className="flex-1 overflow-y-auto py-2" data-testid="full-log">
            {searchResults !== null ? (
              <>
                <p className="text-xs text-muted-foreground px-3 pb-1">{searchResults.length} result{searchResults.length !== 1 ? "s" : ""} for “{search}”</p>
                {searchResults.length === 0 && <p className="text-center text-sm text-muted-foreground py-10">No matches.</p>}
                {searchResults.map((e) => <LogRow key={e.id} e={e} />)}
              </>
            ) : (
              <>
                {full.length === 0 && <p className="text-center text-sm text-muted-foreground py-10">No messages yet.</p>}
                {full.map((e) => <LogRow key={e.id} e={e} />)}
              </>
            )}
          </div>
        </div>

        <div className="bg-[#0a0a0a] border border-white/10 rounded-lg overflow-hidden flex flex-col h-[520px]">
          <div className="px-4 py-3 border-b border-white/10 bg-black/40 flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-primary" />
            <h3 className="font-heading font-700 text-sm">PancakeCupCake_ Conversation Log</h3>
          </div>
          <div className="flex-1 overflow-y-auto py-2" data-testid="bot-log">
            {bot.length === 0 && <p className="text-center text-sm text-muted-foreground py-10">No AI chatter yet. Try <span className="text-primary">!talk</span></p>}
            {bot.map((e) => <LogRow key={e.id} e={e} />)}
          </div>
        </div>
      </div>
    </div>
  );
}
